import React from 'react';
import {
  Box,
  Alert,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  CircularProgress,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Checkbox,
} from '@mui/material';
import { Search as SearchIcon, PictureAsPdf as PdfIcon, Print as PrintIcon, MailOutline as MailIcon, TableView as ExcelIcon } from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import PageLayout from '../components/common/PageLayout';
import { get } from '../utils/api';
import ThankYouEmailDialog from '../components/reports/ThankYouEmailDialog';

const formatDate = (d) => {
  if (!d) return '-';
  try {
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? '-' : dt.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
  } catch {
    return '-';
  }
};

const formatDateOrder = (d) => {
  if (!d) return '-';
  try {
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? '-' : dt.toISOString().slice(0, 10);
  } catch {
    return '-';
  }
};

const formatCurrency = (n) => {
  const num = parseFloat(n);
  if (isNaN(num)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
};

const eventItemKey = (productId, variationId) => {
  const productKey = Number(productId || 0);
  const variationKey = Number(variationId || 0);
  return Number.isFinite(variationKey) && variationKey > 0
    ? `${productKey}::${variationKey}`
    : `${productKey}::product`;
};
const formatEventVariationOptionLabel = (label, productName) => {
  const trimmed = String(label || "").trim();
  const baseName = String(productName || "").trim();
  if (!trimmed) return "";
  if (!baseName) return trimmed;
  const prefix = `${baseName} - `;
  if (trimmed.toLowerCase().startsWith(prefix.toLowerCase())) {
    return trimmed.slice(prefix.length).trim();
  }
  return trimmed;
};
const normalizedEventItemLabel = (label, productName) => formatEventVariationOptionLabel(label, productName).trim();

/** Pledge rows use Woo order total; count each pledge order_id once when summing (multi-line edge case). */
function sumEventReportRowAmounts(rows) {
  const seenPledgeOrder = new Set();
  return (rows || []).reduce((sum, r) => {
    const a = parseFloat(r.amount) || 0;
    const st = String(r.status || '').toLowerCase();
    if (st === 'pledge-pending' || st === 'pledge-completed') {
      const oid = Number(r.order_id);
      if (Number.isFinite(oid) && seenPledgeOrder.has(oid)) return sum;
      if (Number.isFinite(oid)) seenPledgeOrder.add(oid);
    }
    return sum + a;
  }, 0);
}

const todayStr = () => new Date().toISOString().slice(0, 10);
const reportActionIconSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
};
const DATE_RANGE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'last_12_months', label: 'Last 12 months' },
  { value: 'year_to_date', label: 'Year to date (YTD)' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_90_days', label: 'Last 90 days' },
  { value: 'week_to_date', label: 'Week to date' },
  { value: 'month_to_date', label: 'Month to date' },
  { value: 'custom', label: 'Custom' },
];

function formatLocalDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function resolveDateRange(range, customStart, customEnd) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const out = { start: null, end: null };

  if (range === 'all') return out;
  if (range === 'custom') return { start: customStart || null, end: customEnd || null };
  if (range === 'today') {
    const d = formatLocalDate(today);
    return { start: d, end: d };
  }
  if (range === 'yesterday') {
    const d = new Date(today);
    d.setDate(d.getDate() - 1);
    const iso = formatLocalDate(d);
    return { start: iso, end: iso };
  }
  if (range === 'last_7_days') {
    const s = new Date(today);
    s.setDate(s.getDate() - 6);
    return { start: formatLocalDate(s), end: formatLocalDate(today) };
  }
  if (range === 'last_30_days') {
    const s = new Date(today);
    s.setDate(s.getDate() - 29);
    return { start: formatLocalDate(s), end: formatLocalDate(today) };
  }
  if (range === 'last_90_days') {
    const s = new Date(today);
    s.setDate(s.getDate() - 89);
    return { start: formatLocalDate(s), end: formatLocalDate(today) };
  }
  if (range === 'week_to_date') {
    const s = new Date(today);
    s.setDate(s.getDate() - s.getDay()); // Sunday-based week
    return { start: formatLocalDate(s), end: formatLocalDate(today) };
  }
  if (range === 'month_to_date') {
    const s = new Date(today.getFullYear(), today.getMonth(), 1);
    return { start: formatLocalDate(s), end: formatLocalDate(today) };
  }
  if (range === 'year_to_date') {
    const s = new Date(today.getFullYear(), 0, 1);
    return { start: formatLocalDate(s), end: formatLocalDate(today) };
  }
  if (range === 'last_12_months') {
    const s = new Date(today);
    s.setFullYear(s.getFullYear() - 1);
    s.setDate(s.getDate() + 1);
    return { start: formatLocalDate(s), end: formatLocalDate(today) };
  }
  return out;
}

// Optional: set REACT_APP_EVENT_REPORT_CATEGORY_SLUGS=slug1,slug2 in .env to limit product search to those categories
const eventReportCategorySlugs = (typeof process.env.REACT_APP_EVENT_REPORT_CATEGORY_SLUGS === 'string'
  ? process.env.REACT_APP_EVENT_REPORT_CATEGORY_SLUGS.trim()
  : '') || '';

export default function EventReportsPage() {
  const [searchParams] = useSearchParams();
  const rawScope = (searchParams.get('scope') || '').toLowerCase();
  const scope = rawScope === 'temple' ? 'temple' : null;
  const title = scope === 'temple' ? 'Temple Event Reports' : 'Event Reports';

  const [productOptions, setProductOptions] = React.useState([]);
  const [selectedProduct, setSelectedProduct] = React.useState(null);
  const [productInput, setProductInput] = React.useState('');
  const [dateRange, setDateRange] = React.useState('last_90_days');
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState(todayStr());
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [searched, setSearched] = React.useState(false);
  // Second-level filters (applied client-side after first search)
  const [nameFilter, setNameFilter] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');
  const [itemFilter, setItemFilter] = React.useState('');
  const [linkedSections, setLinkedSections] = React.useState([]);
  const [linkedLoading, setLinkedLoading] = React.useState(false);
  const [thankYouOpen, setThankYouOpen] = React.useState(false);
  const [thankYouMessage, setThankYouMessage] = React.useState('');
  const [singleEmailOpen, setSingleEmailOpen] = React.useState(false);
  const [singleEmailConfig, setSingleEmailConfig] = React.useState(null);
  const [paymentLinksByProduct, setPaymentLinksByProduct] = React.useState({});
  const [selectedRecipientKeys, setSelectedRecipientKeys] = React.useState({});
  const tableRef = React.useRef(null);
  const printRef = React.useRef(null);
  const linkedPrintRefs = React.useRef({});

  const fetchProducts = React.useCallback(async (q) => {
    if (!q || q.length < 2) {
      setProductOptions([]);
      return;
    }
    try {
      const params = { q, limit: 50, for_event_report: '1' };
      if (eventReportCategorySlugs) params.parent_category_slugs = eventReportCategorySlugs;
      const res = await get('/ldb/facets/products', params);
      const raw = Array.isArray(res?.products) ? res.products : [];
      const seen = new Set();
      const deduped = raw.filter((p) => {
        const key = String(p?.name || '').trim().toLowerCase();
        if (!key) return false;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setProductOptions(deduped);
    } catch {
      setProductOptions([]);
    }
  }, []);

  const loadReport = React.useCallback(async () => {
    const ids = selectedProduct?.id ? [selectedProduct.id] : [];
    if (ids.length === 0) {
      setError('Select at least one event (product).');
      return;
    }
    const range = resolveDateRange(dateRange, startDate, endDate);
    if (dateRange === 'custom' && (!range.start || !range.end)) {
      setError('Please select both start and end date.');
      return;
    }
    setError('');
    setLoading(true);
    setLinkedLoading(true);
    setSearched(true);
    setNameFilter('');
    setStatusFilter('');
    setItemFilter('');
    setLinkedSections([]);
    try {
      const params = {
        product_ids: ids.join(','),
      };
      if (scope) params.scope = scope;
      if (range.start) params.start_date = range.start;
      if (range.end) params.end_date = range.end;
      const [eventRes, linkedRes] = await Promise.all([
        get('/ldb/reports/event-report', params),
        get('/ldb/reports/event-report-linked-sections', { event_product_ids: ids.join(','), ...(scope ? { scope } : {}), ...(range.start ? { start_date: range.start } : {}), ...(range.end ? { end_date: range.end } : {}) }),
      ]);
      setData(eventRes.data || []);
      setLinkedSections(Array.isArray(linkedRes?.data) ? linkedRes.data : []);
    } catch (e) {
      setError(e?.message || 'Failed to load report');
      setData([]);
      setLinkedSections([]);
    } finally {
      setLoading(false);
      setLinkedLoading(false);
    }
  }, [selectedProduct, dateRange, startDate, endDate]);
  React.useEffect(() => {
    setSelectedRecipientKeys({});
  }, [selectedProduct?.id, dateRange, startDate, endDate, data.length, linkedSections.length, nameFilter, statusFilter, itemFilter]);

  const reportTitle = selectedProduct?.name || 'Event Report';

  // Second-level filter: client-side on loaded data
  const filteredData = React.useMemo(() => {
    let rows = data;
    const nameQ = nameFilter.trim().toLowerCase();
    if (nameQ) {
      rows = rows.filter(r =>
        String(r.name || '').toLowerCase().includes(nameQ) ||
        String(r.email || '').toLowerCase().includes(nameQ)
      );
    }
    if (statusFilter.trim()) {
      const s = statusFilter.trim().toLowerCase();
      rows = rows.filter(r => String(r.status || '').toLowerCase() === s);
    }
    if (itemFilter.trim()) {
      const selectedProductName = String(selectedProduct?.name || '').trim();
      rows = rows.filter((r) => normalizedEventItemLabel(r.item, selectedProductName) === itemFilter.trim());
    }
    return rows;
  }, [data, nameFilter, statusFilter, itemFilter, selectedProduct?.name]);

  const itemSummary = React.useMemo(() => {
    const byItem = new Map();
    for (const r of filteredData) {
      const key = (r.item || '').trim() || '(blank)';
      if (!byItem.has(key)) byItem.set(key, []);
      byItem.get(key).push(r);
    }
    return Array.from(byItem.entries())
      .map(([item, rowList]) => ({
        item,
        count: rowList.length,
        amount: sumEventReportRowAmounts(rowList),
      }))
      .sort((a, b) => (b.amount - a.amount));
  }, [filteredData]);

  const itemOptions = React.useMemo(() => {
    const map = new Map();
    const selectedProductName = String(selectedProduct?.name || '').trim();
    data.forEach((r) => {
      const label = normalizedEventItemLabel(r.item, selectedProductName);
      if (label && !map.has(label)) map.set(label, { key: label, label });
    });
    (linkedSections || []).forEach((section) => {
      const productName = String(section?.event_product_name || '').trim();
      const names = Array.isArray(section?.mapped_event_variation_names) ? section.mapped_event_variation_names : [];
      if (names.length === 0) {
        const label = normalizedEventItemLabel(productName, productName);
        if (label && !map.has(label)) map.set(label, { key: label, label });
        return;
      }
      names.forEach((name) => {
        const label = normalizedEventItemLabel(name, productName);
        if (label && !map.has(label)) map.set(label, { key: label, label });
      });
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [data, linkedSections, selectedProduct?.name]);

  const distinctStatuses = React.useMemo(() => {
    const set = new Set();
    data.forEach(r => { const v = (r.status || '').trim(); if (v) set.add(v); });
    (linkedSections || []).forEach((section) => {
      (section.rows || []).forEach((r) => {
        const v = String(r.status || '').trim();
        if (v) set.add(v);
      });
    });
    return Array.from(set).sort();
  }, [data, linkedSections]);

  const filteredLinkedSections = React.useMemo(() => {
    const nameQ = nameFilter.trim().toLowerCase();
    const statusQ = statusFilter.trim().toLowerCase();
    const itemQ = itemFilter.trim();
    return (linkedSections || [])
      .filter((section) => {
        if (!itemQ) return true;
        const productName = String(section?.event_product_name || '').trim();
        const names = Array.isArray(section?.mapped_event_variation_names) ? section.mapped_event_variation_names : [];
        if (names.length === 0) return normalizedEventItemLabel(productName, productName) === itemQ;
        return names.some((name) => normalizedEventItemLabel(name, productName) === itemQ);
      })
      .map((section) => {
        let rows = Array.isArray(section?.rows) ? section.rows : [];
        if (nameQ) {
          rows = rows.filter((r) =>
            String(r.name || '').toLowerCase().includes(nameQ) ||
            String(r.email || '').toLowerCase().includes(nameQ)
          );
        }
        if (statusQ) {
          rows = rows.filter((r) => String(r.status || '').toLowerCase() === statusQ);
        }
        return {
          ...section,
          rows,
          count: rows.length,
          amount: sumEventReportRowAmounts(rows),
        };
      })
      .filter((s) => s.count > 0);
  }, [linkedSections, nameFilter, statusFilter, itemFilter]);

  const filteredCount = filteredData.length;
  const filteredAmount = sumEventReportRowAmounts(filteredData);
  const linkedCount = filteredLinkedSections.reduce((sum, s) => sum + Number(s.count || 0), 0);
  const linkedAmount = filteredLinkedSections.reduce((sum, s) => sum + Number(s.amount || 0), 0);
  const pdfRowCount = filteredCount + linkedCount;
  const pdfTotalAmount = filteredAmount + linkedAmount;
  const selectedDateRangeLabel = React.useMemo(() => {
    const opt = DATE_RANGE_OPTIONS.find((o) => o.value === dateRange);
    if (dateRange === 'custom') {
      if (startDate && endDate) return `Custom (${startDate} to ${endDate})`;
      return 'Custom';
    }
    return opt?.label || 'All';
  }, [dateRange, startDate, endDate]);

  const emailFromRow = React.useCallback((row) => String(row?.email || '').trim().toLowerCase(), []);
  const selectableRows = React.useMemo(() => {
    const direct = filteredData.map((row, idx) => ({ row, key: `direct:${idx}` }));
    const linked = filteredLinkedSections.flatMap((section) => (
      (section.rows || []).map((row, idx) => ({ row, key: `linked:${section.link_id}:${idx}` }))
    ));
    return [...direct, ...linked].filter(({ row }) => emailFromRow(row).includes('@'));
  }, [filteredData, filteredLinkedSections, emailFromRow]);
  const allSelected = selectableRows.length > 0 && selectableRows.every(({ key }) => !!selectedRecipientKeys[key]);
  const selectedRows = React.useMemo(() => (
    selectableRows.filter(({ key }) => !!selectedRecipientKeys[key]).map(({ row }) => row)
  ), [selectableRows, selectedRecipientKeys]);
  const selectedRecipients = React.useMemo(() => {
    const out = new Set();
    selectedRows.forEach((row) => {
      const email = emailFromRow(row);
      if (email && email.includes('@')) out.add(email);
    });
    return Array.from(out);
  }, [selectedRows, emailFromRow]);
  const isPaidStatus = React.useCallback((status) => {
    const s = String(status || '').trim().toLowerCase();
    return ['completed', 'processing', 'on-hold', 'paid', 'pledge-completed'].includes(s);
  }, []);
  const selectedRecipientContexts = React.useMemo(() => {
    const out = {};
    selectedRows.forEach((row) => {
      const email = emailFromRow(row);
      if (!email || !email.includes('@')) return;
      const amount = Number(row?.amount || 0);
      const paid = isPaidStatus(row?.status) ? amount : 0;
      const balance = Math.max(0, amount - paid);
      const productId = Number(row?.product_id || 0);
      out[email] = {
        reportTitle,
        recipientName: row?.name || 'Sponsor',
        productName: row?.sponsorship || row?.item || reportTitle || 'Event Sponsorship',
        sponsorshipAmount: amount,
        paymentsMade: paid,
        balanceAmount: balance,
        paymentLink: productId ? (paymentLinksByProduct[productId] || '') : '',
      };
    });
    return out;
  }, [selectedRows, emailFromRow, isPaidStatus, paymentLinksByProduct, reportTitle]);

  const ensurePaymentLink = React.useCallback(async (productId) => {
    const id = Number(productId || 0);
    if (!id) return '';
    if (paymentLinksByProduct[id] !== undefined) return paymentLinksByProduct[id] || '';
    try {
      const res = await get(`/ldb/products/${id}`);
      const link = String(res?.product?.product_url || '').trim();
      setPaymentLinksByProduct((prev) => ({ ...prev, [id]: link }));
      return link;
    } catch {
      setPaymentLinksByProduct((prev) => ({ ...prev, [id]: '' }));
      return '';
    }
  }, [paymentLinksByProduct]);

  const openSingleRecipientDialog = async (row) => {
    const email = String(row?.email || '').trim().toLowerCase();
    if (!email) return;
    const amount = Number(row?.amount || 0);
    const paid = isPaidStatus(row?.status) ? amount : 0;
    const balance = Math.max(0, amount - paid);
    const productId = Number(row?.product_id || 0);
    const paymentLink = productId ? await ensurePaymentLink(productId) : '';
    setSingleEmailConfig({
      recipients: [email],
      context: {
        reportTitle,
        recipientName: row?.name || 'Sponsor',
        productName: row?.sponsorship || row?.item || reportTitle || 'Event Sponsorship',
        sponsorshipAmount: amount,
        paymentsMade: paid,
        balanceAmount: balance,
        paymentLink,
      },
    });
    setSingleEmailOpen(true);
  };
  const toggleSelectAll = (checked) => {
    if (!checked) {
      setSelectedRecipientKeys({});
      return;
    }
    const next = {};
    selectableRows.forEach(({ key }) => { next[key] = true; });
    setSelectedRecipientKeys(next);
  };
  const toggleRowSelection = (rowKey, checked) => {
    setSelectedRecipientKeys((prev) => {
      const next = { ...prev };
      if (checked) next[rowKey] = true;
      else delete next[rowKey];
      return next;
    });
  };

  const exportHeaders = React.useMemo(() => ([
    'Gothram',
    'Name',
    'Spouse',
    'Family Members',
    'Sponsorship',
    'Item',
    'Amount',
    'Status',
    'Receipt Date',
    'Date of Order',
  ]), []);
  const rowToPdfCells = (r) => [
    String(r.gothram || '').slice(0, 20),
    String(r.name || '').slice(0, 25),
    String(r.spouse || '-').slice(0, 15),
    String(r.family_members || '-').slice(0, 30),
    String(r.sponsorship || '').slice(0, 25),
    String(r.item || '').slice(0, 30),
    formatCurrency(r.amount),
    String(r.status || ''),
    formatDate(r.receipt_date),
    formatDateOrder(r.date_of_order),
  ];
  const getExportRows = React.useCallback(() => {
    const eventRows = filteredData.map(rowToPdfCells);
    const linkedRows = filteredLinkedSections.flatMap((section) => (section.rows || []).map(rowToPdfCells));
    return [...eventRows, ...linkedRows];
  }, [filteredData, filteredLinkedSections]);

  const handleDownloadPDF = () => {
    const allRows = getExportRows();
    if (allRows.length === 0) return;
    const totalCount = allRows.length;
    const totalAmt = filteredAmount + filteredLinkedSections.reduce((s, section) => s + Number(section.amount || 0), 0);

    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const title = reportTitle;
    doc.setFontSize(14);
    doc.text(title, 14, 22);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()} | Count: ${totalCount} | Total: ${formatCurrency(totalAmt)}`, 14, 36);

    autoTable(doc, {
      head: [exportHeaders],
      body: allRows,
      startY: 44,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [255, 193, 7] },
    });
    doc.save(`${title.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`);
  };
  const handleDownloadExcel = () => {
    const allRows = getExportRows();
    if (allRows.length === 0) return;

    const totalCount = allRows.length;
    const totalAmt = filteredAmount + filteredLinkedSections.reduce((s, section) => s + Number(section.amount || 0), 0);
    const timestamp = new Date();
    const sheet = XLSX.utils.aoa_to_sheet([
      [reportTitle],
      [`Generated: ${timestamp.toLocaleString()}`],
      [`Count: ${totalCount}`],
      [`Total: ${formatCurrency(totalAmt)}`],
      [],
      exportHeaders,
      ...allRows,
    ]);

    sheet['!cols'] = exportHeaders.map((header, idx) => {
      const values = allRows.map((row) => String(row[idx] || ''));
      const maxValueLength = values.reduce((max, value) => Math.max(max, value.length), header.length);
      return { wch: Math.min(Math.max(maxValueLength + 2, 14), 32) };
    });

    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Event Report');
    XLSX.writeFile(book, `${reportTitle.replace(/[^a-z0-9]/gi, '_')}_${timestamp.toISOString().slice(0, 10)}.xlsx`);
  };

  const handlePrint = () => {
    if (!printRef.current) return;
    const mainContent = printRef.current.innerHTML;
    const linkedContent = Object.values(linkedPrintRefs.current || {})
      .filter(Boolean)
      .map((el) => el.innerHTML)
      .join('<div style="margin-top: 24px;"></div>');
    const printContent = linkedContent ? `${mainContent}<div style="margin-top: 24px;">${linkedContent}</div>` : mainContent;
    const win = window.open('', '_blank');
    win.document.write(`
      <!DOCTYPE html><html><head><title>${reportTitle}</title>
      <style>
        body { font-family: Arial,sans-serif; padding: 16px; }
        table { border-collapse: collapse; width: 100%; font-size: 12px; }
        th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
        th { background: #ffc107; }
        .summary { margin-bottom: 16px; }
      </style></head><body>${printContent}</body></html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 250);
  };

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
        {title}
      </Typography>

      {/* Step 1: Event + date range (required) */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>Search by event and date range</Typography>
        <Stack spacing={2} direction={{ xs: 'column', md: 'row' }} flexWrap="wrap" alignItems="flex-start">
          <Autocomplete
            size="small"
            options={productOptions}
            value={selectedProduct}
            onChange={(_, v) => setSelectedProduct(v)}
            inputValue={productInput}
            onInputChange={(_, v) => {
              setProductInput(v);
              fetchProducts(v);
            }}
            getOptionLabel={(opt) => (opt && opt.name) ? `${opt.name}` : ''}
            filterOptions={(options, state) => {
              const q = String(state?.inputValue || '').trim().toLowerCase();
              if (!q) return options;
              return options.filter((o) => String(o?.name || '').toLowerCase().includes(q));
            }}
            isOptionEqualToValue={(a, b) => a && b && a.id === b.id}
            renderInput={(params) => (
              <TextField {...params} label="Event (product)" placeholder="Type to search event name" />
            )}
            sx={{ minWidth: 280 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Date Range</InputLabel>
            <Select value={dateRange} label="Date Range" onChange={e => setDateRange(e.target.value)}>
              {DATE_RANGE_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {dateRange === 'custom' ? (
            <>
              <TextField
                label="Start date"
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                size="small"
                required
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 160 }}
              />
              <TextField
                label="End date"
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                size="small"
                required
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 160 }}
              />
            </>
          ) : null}
          <Button variant="contained" onClick={loadReport} disabled={loading} startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}>
            {loading ? 'Loading...' : 'Search'}
          </Button>
        </Stack>
      </Paper>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      {searched && !loading && (
        <>
          {thankYouMessage ? (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setThankYouMessage('')}>
              {thankYouMessage}
            </Alert>
          ) : null}
          {/* Step 2: Refine with name, status, item */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>Refine results</Typography>
            <Stack spacing={2} direction={{ xs: 'column', md: 'row' }} flexWrap="wrap" alignItems="flex-start">
              <TextField
                label="Name"
                size="small"
                value={nameFilter}
                onChange={e => setNameFilter(e.target.value)}
                placeholder="Search by name or email"
                sx={{ minWidth: 200 }}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {distinctStatuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Product Variation</InputLabel>
                <Select value={itemFilter} label="Product Variation" onChange={e => setItemFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {itemOptions.map((option) => <MenuItem key={option.key} value={option.key}>{option.label.length > 50 ? option.label.slice(0, 47) + '...' : option.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
          </Paper>

          {/* Direct sponsors summary + benefitted sponsors summary */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start" sx={{ '& > *': { flex: '1 1 0', minWidth: 0 } }}>
              <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Direct Sponsors - Summary by item</Typography>
                <Table size="small" sx={{ width: '100%', maxWidth: '100%' }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell>Item</TableCell>
                      <TableCell align="right">Count</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {itemSummary.length === 0 ? (
                      <TableRow><TableCell colSpan={3}>No records</TableCell></TableRow>
                    ) : (
                      <>
                        {itemSummary.map(row => (
                          <TableRow key={row.item}>
                            <TableCell sx={{ maxWidth: 400 }} title={row.item}>{String(row.item).slice(0, 60)}{row.item.length > 60 ? '...' : ''}</TableCell>
                            <TableCell align="right">{row.count}</TableCell>
                            <TableCell align="right">{formatCurrency(row.amount)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ bgcolor: 'grey.200', fontWeight: 600 }}>
                          <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{itemSummary.reduce((s, r) => s + r.count, 0)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(itemSummary.reduce((s, r) => s + r.amount, 0))}</TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </Box>
              <Paper variant="outlined" sx={{ p: 2, flex: '1 1 0', minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                  Benefited Sponsors
                </Typography>
                {linkedLoading ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={16} />
                    <Typography variant="caption">Loading linked sponsors…</Typography>
                  </Stack>
                ) : filteredLinkedSections.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No linked sponsor sections configured for selected event.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {filteredLinkedSections.map((s) => (
                      <Typography key={s.link_id} variant="body2">
                        {s.section_label} ({s.count} sponsor{s.count === 1 ? '' : 's'})
                      </Typography>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Stack>
          </Paper>

          {/* PDF & Print + detail table */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
            <Tooltip title="Download PDF">
              <span>
                <IconButton
                  color="error"
                  onClick={handleDownloadPDF}
                  disabled={pdfRowCount === 0}
                  sx={reportActionIconSx}
                  aria-label="Download PDF"
                >
                  <PdfIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Download Excel">
              <span>
                <IconButton
                  color="success"
                  onClick={handleDownloadExcel}
                  disabled={pdfRowCount === 0}
                  sx={reportActionIconSx}
                  aria-label="Download Excel"
                >
                  <ExcelIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Print report">
              <span>
                <IconButton
                  color="primary"
                  onClick={handlePrint}
                  disabled={pdfRowCount === 0}
                  sx={reportActionIconSx}
                  aria-label="Print report"
                >
                  <PrintIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              size="small"
              startIcon={<MailIcon />}
              onClick={() => setThankYouOpen(true)}
              disabled={selectedRecipients.length === 0}
            >
              Send Email ({selectedRecipients.length})
            </Button>
            <Typography variant="body2" color="text.secondary">
              Showing {pdfRowCount} record{pdfRowCount !== 1 ? 's' : ''} · {formatCurrency(pdfTotalAmount)} total
              {' '}({filteredCount} direct, {linkedCount} benefited)
            </Typography>
          </Stack>

          <Box ref={tableRef} sx={{ overflow: 'auto' }}>
            <Box ref={printRef}>
              {filteredCount > 0 && <Typography variant="h6" sx={{ mb: 1 }}>Direct Sponsors - {reportTitle}</Typography>}
              <Stack direction="row" spacing={2} sx={{ mb: 1 }} className="summary">
                <Typography variant="body2"><strong>Direct Sponsor Count:</strong> {filteredCount}</Typography>
                <Typography variant="body2"><strong>Direct Sponsor Amount:</strong> {formatCurrency(filteredAmount)}</Typography>
              </Stack>
              <Table size="small" stickyHeader sx={{ minWidth: 1200 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'warning.light' }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={allSelected}
                          indeterminate={!allSelected && Object.keys(selectedRecipientKeys).length > 0}
                          onChange={(e) => toggleSelectAll(e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>Gothram</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Spouse</TableCell>
                    <TableCell>Family Members</TableCell>
                    <TableCell>Sponsorship</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Receipt Date</TableCell>
                    <TableCell>Date of Order</TableCell>
                    <TableCell align="center">Email</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.length === 0 ? (
                    <TableRow><TableCell colSpan={12} align="center">No records</TableCell></TableRow>
                  ) : (
                    filteredData.map((row, idx) => (
                      <TableRow key={`${row.name}-${idx}-${row.item}`}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={!!selectedRecipientKeys[`direct:${idx}`]}
                            disabled={!emailFromRow(row).includes('@')}
                            onChange={(e) => toggleRowSelection(`direct:${idx}`, e.target.checked)}
                          />
                        </TableCell>
                        <TableCell>{row.gothram || '-'}</TableCell>
                        <TableCell>{row.name || '-'}</TableCell>
                        <TableCell>{row.spouse || '-'}</TableCell>
                        <TableCell sx={{ maxWidth: 200 }} title={row.family_members}>{row.family_members || '-'}</TableCell>
                        <TableCell sx={{ maxWidth: 180 }} title={row.sponsorship}>{row.sponsorship || '-'}</TableCell>
                        <TableCell sx={{ maxWidth: 200 }} title={row.item}>{row.item || '-'}</TableCell>
                        <TableCell>{formatCurrency(row.amount)}</TableCell>
                        <TableCell>{row.status || '-'}</TableCell>
                        <TableCell>{formatDate(row.receipt_date)}</TableCell>
                        <TableCell>{formatDateOrder(row.date_of_order)}</TableCell>
                        <TableCell align="center">
                          {row.email ? (
                            <Tooltip title="Send email to this sponsor">
                              <IconButton size="small" onClick={() => openSingleRecipientDialog(row)}>
                                <MailIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </Box>
          {filteredLinkedSections.map((section) => (
            <Box
              key={section.link_id}
              ref={(el) => {
                if (el) linkedPrintRefs.current[section.link_id] = el;
                else delete linkedPrintRefs.current[section.link_id];
              }}
              sx={{ mt: 3 }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Benefited Sponsors - {section.section_label}
              </Typography>
              <Box sx={{ overflow: 'auto' }}>
                <Table size="small" stickyHeader sx={{ minWidth: 1200 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.200' }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={allSelected}
                          indeterminate={!allSelected && Object.keys(selectedRecipientKeys).length > 0}
                          onChange={(e) => toggleSelectAll(e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>Gothram</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Spouse</TableCell>
                      <TableCell>Family Members</TableCell>
                      <TableCell>Sponsorship</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Receipt Date</TableCell>
                      <TableCell>Date of Order</TableCell>
                      <TableCell align="center">Email</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {section.rows.length === 0 ? (
                      <TableRow><TableCell colSpan={12} align="center">No records</TableCell></TableRow>
                    ) : (
                      section.rows.map((row, idx) => (
                        <TableRow key={`linked-${section.link_id}-${row.name}-${idx}-${row.item}`}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={!!selectedRecipientKeys[`linked:${section.link_id}:${idx}`]}
                              disabled={!emailFromRow(row).includes('@')}
                              onChange={(e) => toggleRowSelection(`linked:${section.link_id}:${idx}`, e.target.checked)}
                            />
                          </TableCell>
                          <TableCell>{row.gothram || '-'}</TableCell>
                          <TableCell>{row.name || '-'}</TableCell>
                          <TableCell>{row.spouse || '-'}</TableCell>
                          <TableCell sx={{ maxWidth: 200 }} title={row.family_members}>{row.family_members || '-'}</TableCell>
                          <TableCell sx={{ maxWidth: 180 }} title={row.sponsorship}>{row.sponsorship || '-'}</TableCell>
                          <TableCell sx={{ maxWidth: 200 }} title={row.item}>{row.item || '-'}</TableCell>
                          <TableCell>{formatCurrency(row.amount)}</TableCell>
                          <TableCell>{row.status || '-'}</TableCell>
                          <TableCell>{formatDate(row.receipt_date)}</TableCell>
                          <TableCell>{formatDateOrder(row.date_of_order)}</TableCell>
                          <TableCell align="center">
                            {row.email ? (
                              <Tooltip title="Send email to this sponsor">
                                <IconButton size="small" onClick={() => openSingleRecipientDialog(row)}>
                                  <MailIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : '-'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          ))}
        </>
      )}

      {!searched && (
        <Typography color="text.secondary">Select an event (product) and date range, then click Search. Start and end dates are required only for Custom.</Typography>
      )}
      <ThankYouEmailDialog
        open={thankYouOpen}
        onClose={() => setThankYouOpen(false)}
        reportType="event"
        context={{
          reportTitle,
          dateRangeLabel: selectedDateRangeLabel,
        }}
        recipients={selectedRecipients}
        recipientContexts={selectedRecipientContexts}
        onSent={(res) => {
          const sentCount = Number(res?.recipient_count || selectedRecipients.length);
          const mode = String(res?.delivery_mode || '').toLowerCase() === 'individual' ? 'Individual' : 'BCC';
          setThankYouMessage(`Email sent successfully (${mode}). Recipients: ${sentCount}.`);
          setSelectedRecipientKeys({});
        }}
      />
      <ThankYouEmailDialog
        open={singleEmailOpen}
        onClose={() => { setSingleEmailOpen(false); setSingleEmailConfig(null); }}
        reportType="event"
        initialEmailType="event_reminder"
        context={singleEmailConfig?.context || {}}
        recipients={singleEmailConfig?.recipients || []}
        onSent={() => {
          setThankYouMessage('Individual email sent successfully.');
        }}
      />
    </PageLayout>
  );
}







